import _extends from "@babel/runtime/helpers/extends";
import React, { useRef, forwardRef, useImperativeHandle, useEffect, useMemo, useState, useCallback } from 'react';
import { cls, execRAF, getActualContainer, getScrollContainerRect } from '@arco-design/mobile-utils';
import { ContextLayout } from '../context-provider';
import { useRefState } from '../_helpers';
import Portal from '../portal';

/**
 * 粘性布局组件，元素相对于窗口或指定容器的吸顶效果。
 * @en Sticky layout component, The sticky-to-top effect of the element relative to the window or specified container
 * @type 布局
 * @type_en Layout
 * @name 粘性布局
 * @name_en Sticky
 */
var Sticky = /*#__PURE__*/forwardRef(function (props, ref) {
  var _props$position = props.position,
      position = _props$position === void 0 ? 'top' : _props$position,
      _props$topOffset = props.topOffset,
      topOffset = _props$topOffset === void 0 ? 0 : _props$topOffset,
      _props$bottomOffset = props.bottomOffset,
      bottomOffset = _props$bottomOffset === void 0 ? 0 : _props$bottomOffset,
      _props$followOffset = props.followOffset,
      followOffset = _props$followOffset === void 0 ? 0 : _props$followOffset,
      children = props.children,
      className = props.className,
      style = props.style,
      _props$zIndex = props.zIndex,
      zIndex = _props$zIndex === void 0 ? 100 : _props$zIndex,
      _props$portalWhenStic = props.portalWhenSticky,
      portalWhenSticky = _props$portalWhenStic === void 0 ? false : _props$portalWhenStic,
      _props$stickyStyle = props.stickyStyle,
      stickyStyle = _props$stickyStyle === void 0 ? 'fixed' : _props$stickyStyle,
      userSetStickyCssStyle = props.stickyCssStyle,
      getPortalContainer = props.getPortalContainer,
      getContainer = props.getContainer,
      getScrollContainer = props.getScrollContainer,
      onStickyStateChange = props.onStickyStateChange,
      onTopChange = props.onTopChange;

  var _useRefState = useRefState(false),
      isSticky = _useRefState[0],
      isStickyRef = _useRefState[1],
      setIsSticky = _useRefState[2];

  var _useRefState2 = useRefState(false),
      wasSticky = _useRefState2[0],
      wasStickyRef = _useRefState2[1],
      setWasSticky = _useRefState2[2];

  var _useState = useState({}),
      stickyStateStyle = _useState[0],
      setStickyStateStyle = _useState[1];

  var contentRef = useRef(null);
  var placeholderRef = useRef(null);
  var contentCalculateHeightRef = useRef(0);
  var containerRef = useRef(null);
  var scrollContainerRef = useRef(null);
  var framePendingRef = useRef(false);
  var containerEventHandler = useCallback(function (_ref) {
    var followTop = _ref.followTop,
        followBottom = _ref.followBottom;

    if (!contentRef.current) {
      return;
    }

    var needTop = position === 'top' || position === 'both';
    var needBottom = position === 'bottom' || position === 'both';
    var placeholderClientRect = placeholderRef.current.getBoundingClientRect();
    var contentClientRect = contentRef.current.getBoundingClientRect();
    var calculatedHeight = contentClientRect.height;
    contentCalculateHeightRef.current = contentClientRect.height;

    var _getScrollContainerRe = getScrollContainerRect(scrollContainerRef.current),
        containerRect = _getScrollContainerRe.containerRect;

    var containerTop = containerRect.top,
        containerBottom = containerRect.bottom,
        containerHeight = containerRect.height;
    var disFromTop = placeholderClientRect.top - containerTop;
    var disFromBottom = placeholderClientRect.top + calculatedHeight - containerBottom;
    var topFollowDifference = followBottom - followOffset - calculatedHeight - topOffset - containerTop;
    var bottomFollowDifference = containerHeight - followTop - followOffset - calculatedHeight - bottomOffset;
    setWasSticky(Boolean(isStickyRef.current));
    var isTopSticky = needTop ? disFromTop <= topOffset && followBottom > containerTop + followOffset : false;
    var isBottomSticky = needBottom ? disFromBottom >= -bottomOffset && followTop < containerBottom - followOffset : false;
    var newStickyState = isTopSticky || isBottomSticky;
    var cssTop = (stickyStyle === 'absolute' ? 0 : containerTop) + topOffset;
    var cssBottom = (stickyStyle === 'absolute' ? 0 : window.innerHeight - containerBottom) + bottomOffset;
    var stickyCssStyle = {};

    if (newStickyState) {
      stickyCssStyle = _extends({
        transform: 'translateZ(0)',
        WebkitTransform: 'translateZ(0)',
        position: stickyStyle === 'absolute' ? 'absolute' : 'fixed',
        zIndex: zIndex
      }, isTopSticky ? {
        top: topFollowDifference > 0 ? cssTop : cssTop + topFollowDifference
      } : {}, isBottomSticky ? {
        bottom: bottomFollowDifference > 0 ? cssBottom : cssBottom + bottomFollowDifference
      } : {}, {
        left: placeholderClientRect.left,
        width: placeholderClientRect.width
      }, userSetStickyCssStyle || {});
    }

    onTopChange == null ? void 0 : onTopChange(Math.max(0, contentClientRect.top - containerTop - topOffset));
    setIsSticky(newStickyState);
    setStickyStateStyle(stickyCssStyle);

    if (newStickyState !== wasStickyRef.current) {
      onStickyStateChange && onStickyStateChange({
        isTopSticky: isTopSticky,
        isBottomSticky: isBottomSticky,
        isSticky: newStickyState,
        wasSticky: wasStickyRef.current
      });
    }
  }, [position, topOffset, bottomOffset, followOffset, zIndex, stickyStyle, onStickyStateChange, onTopChange]);
  var recalculatePosition = useCallback(function () {
    if (framePendingRef.current) {
      return;
    }

    execRAF(function () {
      framePendingRef.current = false;

      if (containerRef.current) {
        var _containerRef$current = containerRef.current.getBoundingClientRect(),
            _top = _containerRef$current.top,
            bottom = _containerRef$current.bottom;

        containerEventHandler({
          followTop: _top,
          followBottom: bottom
        });
      }
    });
    framePendingRef.current = true;
  }, [containerEventHandler]);
  useEffect(function () {
    var containerEle = getActualContainer(getContainer);
    containerRef.current = containerEle || document.body;
    var eventTarget = getScrollContainer ? getActualContainer(getScrollContainer) : window;
    if (!eventTarget) return;
    scrollContainerRef.current = eventTarget;

    var attachEventListeners = function attachEventListeners(target) {
      target.addEventListener('scroll', recalculatePosition);
    };

    var removeEventListeners = function removeEventListeners(target) {
      target.removeEventListener('scroll', recalculatePosition);
    };

    attachEventListeners(eventTarget);
    recalculatePosition();
    return function () {
      removeEventListeners(eventTarget);
    };
  }, [getContainer, getScrollContainer, recalculatePosition]);
  useEffect(function () {
    if (placeholderRef.current) {
      // 当元素吸顶时，默认有一个占位的元素占住该元素的位置，避免布局产生抖动
      // @en When an element is sticky to the top, a placeholder element occupies the element's position by default to avoid jitter in the layout
      placeholderRef.current.style.height = (isStickyRef.current ? contentCalculateHeightRef.current : 0) + "px";
    }
  }, [isSticky, wasSticky]);
  useImperativeHandle(ref, function () {
    return {
      dom: contentRef.current,
      recalculatePosition: recalculatePosition
    };
  }, [recalculatePosition]);
  var computedStyle = useMemo(function () {
    return _extends({}, style || {}, stickyStateStyle);
  }, [stickyStateStyle, style]);

  function renderSticky(prefixCls) {
    return /*#__PURE__*/React.createElement("div", {
      className: cls(prefixCls + "-sticky", className),
      ref: contentRef,
      style: computedStyle
    }, children);
  }

  return /*#__PURE__*/React.createElement(ContextLayout, null, function (_ref2) {
    var prefixCls = _ref2.prefixCls;
    return /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("div", {
      ref: placeholderRef
    }), portalWhenSticky && isSticky ? /*#__PURE__*/React.createElement(Portal, {
      getContainer: getPortalContainer
    }, renderSticky(prefixCls)) : renderSticky(prefixCls));
  });
});
export default Sticky;